import { type ChangeEvent, useCallback, useState } from 'react'

import { ButtonRoot } from '@/components/common/Button'
import { FloatingAnchored } from '@/components/common/Floating'
import { LinkRoute } from '@/components/common/Link'
import { LoadingMask } from '@/components/common/LoadingMask'
import { NoResults } from '@/components/common/NoResults'
import { Icon } from '@/components/icons/Icon'
import { CARD_IMAGE_BASE } from '@/defs/misc'
import { useTypeaheadDynamic } from '@/hooks/useTypeaheadDynamic'
import { useTypeaheadKeyboardNav } from '@/hooks/useTypeaheadKeyboardNav'
import { getSetIcon } from '@/utils/cardUtil'
import { cn } from '@/utils/commonUtil'
import { getHighlightNodes } from '@/utils/typeaheadUtil'

import styles from './Searchbar.module.scss'

type Props = {
  isPrimary?: boolean
  size?: 'lg'
}

export const Searchbar = ({ isPrimary = true, size }: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const { loading, results, search, setSearch } = useTypeaheadDynamic()
  const { onKeyDownButton, onKeyDownInput, refInput, setRefButton } = useTypeaheadKeyboardNav(results.length)

  const hasResults = (loading && results.length > 0) || (!loading && search.length > 2)

  const options = results.map(({ icon: iconSrc, id, image, label, url }) => ({
    icon: url?.includes('/tags/') ? ('tag' as const) : label.includes('(Precon)') ? ('cube' as const) : undefined,
    iconSrc: getSetIcon(iconSrc),
    id,
    image: image ? `${CARD_IMAGE_BASE}/art_crop/front/${image.slice(0, 1)}/${image.slice(1, 2)}/${image}` : undefined,
    label,
    labelDisplay: getHighlightNodes(label, search),
    url,
  }))

  const isPopoverOpen = isOpen && hasResults

  const onFocus = () => {
    setIsOpen(search.length > 2)
  }

  const onChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    const inputText = e.target.value
    setSearch(inputText)
    setIsOpen(inputText.length > 2)
  }

  const onClickClear = useCallback(() => {
    setSearch('')
  }, [setSearch])

  const floatingElement = (
    <div className={styles.dropdown}>
      {results.length > 0 ? (
        <ul>
          {options.map(({ icon, iconSrc, image, label, labelDisplay, url }, i) => (
            <li key={i}>
              <LinkRoute
                className={styles.result}
                href={url}
                onClick={onClickClear}
                onKeyDown={(e) => onKeyDownButton(i, e)}
                ref={(el) => setRefButton(i, el)}
              >
                <div className={styles.resultImage}>
                  {icon ? (
                    <span className={styles.resultIcon}>
                      <Icon name={icon} />
                    </span>
                  ) : iconSrc ? (
                    <span className={styles.resultIconImage}>
                      <Icon src={iconSrc} />
                    </span>
                  ) : image ? (
                    <img alt={`Card art for ${label}`} className={styles.cardArt} src={image} />
                  ) : null}
                </div>
                <span className={styles.resultLabel}>{labelDisplay}</span>
              </LinkRoute>
            </li>
          ))}
        </ul>
      ) : (
        <NoResults />
      )}
      <LoadingMask loading={loading} />
    </div>
  )

  return (
    <div className={cn(styles.container, { loading, size, styles })}>
      <FloatingAnchored
        content={floatingElement}
        isOpen={isPopoverOpen}
        padding='none'
        setIsOpen={setIsOpen}
        width='full'
      />
      <input
        aria-label='Search cards, commanders, and themes'
        autoComplete='off'
        className={styles.input}
        id={isPrimary ? 'searchbar' : undefined}
        name='searchbar'
        onChange={onChangeInput}
        onFocus={onFocus}
        onKeyDown={onKeyDownInput}
        placeholder='Search...'
        ref={refInput}
        type='search'
        value={search}
      />
      <LinkRoute aria-label='EDHREC' className={styles.iconLogo} href='/'>
        <Icon name='brand-edhrec-graphic' />
      </LinkRoute>
      <ButtonRoot aria-label='Search EDHREC' className={styles.iconSearch} onClick={() => refInput.current?.focus()}>
        <span className={styles.iconSearchMagnifyingGlass}>
          <Icon name='magnifyingGlass' />
        </span>
        <span className={styles.iconSearchSpinner}>
          <Icon name='spinner' />
        </span>
      </ButtonRoot>
    </div>
  )
}
